import React from 'react';
import { Link } from 'react-router-dom';
import { partnerSignupUrl } from '../config/partnerPanel';
import { PartnerPanelLink } from '../components/PartnerPanelLink';
import { SiteHero, SiteClosingCta } from '../components/site';
import {
  Users,
  Target,
  Globe,
  CheckCircle,
  ArrowRight,
  Star,
  Lightbulb,
  Shield,
  Zap,
  Heart,
  TrendingUp,
  Users2,
  Award as AwardIcon,
} from 'lucide-react';

export const AboutUs: React.FC = () => {
  return (
    <div className="io-site about-us-page">
      {/* Hero Section */}
      <SiteHero
        centered
        title="About Aeviot"
        subtitle="We're revolutionizing the smart home industry by empowering partners to deliver cutting-edge IoT solutions to their clients."
        visual={
          <div className="io-site-stats">
            <div className="io-site-stat">
              <div className="io-site-stat__value">5+</div>
              <div className="io-site-stat__label">Years Experience</div>
            </div>
            <div className="io-site-stat">
              <div className="io-site-stat__value">500+</div>
              <div className="io-site-stat__label">Partners</div>
            </div>
            <div className="io-site-stat">
              <div className="io-site-stat__value">10,000+</div>
              <div className="io-site-stat__label">Devices Deployed</div>
            </div>
            <div className="io-site-stat">
              <div className="io-site-stat__value">99.9%</div>
              <div className="io-site-stat__label">Uptime</div>
            </div>
          </div>
        }
        primaryCta={{ label: 'Contact Us', to: '/contact-us' }}
      />

      {/* Our Story Section */}
      <section className="our-story">
        <div className="container">
          <div className="section-header">
            <h2>Our Story</h2>
            <p>From startup to industry leader in smart home technology</p>
          </div>
          <div className="story-content">
            <div className="story-text">
              <h3>Founded on Innovation</h3>
              <p>
                Aeviot was founded in 2019 with a simple yet ambitious vision: to make smart home 
                technology accessible to everyone through our partner network. We recognized that 
                while smart home technology was advancing rapidly, many businesses lacked the 
                technical expertise to implement and manage these solutions effectively.
              </p>
              <p>
                Our founders, a team of IoT engineers and business strategists, saw an opportunity 
                to bridge this gap by creating a comprehensive platform that enables partners to offer 
                white-label smart home solutions without requiring extensive technical knowledge.
              </p>
              <div className="story-highlights">
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>2019 - Company Founded</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>2020 - First 100 Partners</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>2021 - Platform Launch</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>2022 - International Expansion</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>2023 - 500+ Partners Milestone</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>2024 - Industry Recognition</span>
                </div>
              </div>
            </div>
            <div className="story-visual">
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-year">2019</div>
                  <div className="timeline-content">
                    <h4>Company Founded</h4>
                    <p>Started with a vision to democratize smart home technology</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-year">2020</div>
                  <div className="timeline-content">
                    <h4>First Partners</h4>
                    <p>Onboarded our first 100 partners across North America</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-year">2021</div>
                  <div className="timeline-content">
                    <h4>Platform Launch</h4>
                    <p>Launched our comprehensive white-label platform</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-year">2022</div>
                  <div className="timeline-content">
                    <h4>Global Expansion</h4>
                    <p>Expanded operations to Europe and Asia-Pacific</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-year">2023</div>
                  <div className="timeline-content">
                    <h4>500+ Partners</h4>
                    <p>Reached milestone of 500+ active partners worldwide</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-year">2024</div>
                  <div className="timeline-content">
                    <h4>Industry Leader</h4>
                    <p>Recognized as leading B2B smart home platform</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="mission-vision">
        <div className="container">
          <div className="section-header">
            <h2>Our Mission & Vision</h2>
            <p>Driving the future of smart home technology through partnership</p>
          </div>
          <div className="mission-content">
            <div className="mission-card">
              <div className="mission-icon">
                <Target size={48} />
              </div>
              <h3>Our Mission</h3>
              <p>
                To empower businesses worldwide with cutting-edge smart home technology solutions, 
                enabling them to deliver exceptional value to their clients while building 
                sustainable, profitable partnerships.
              </p>
              <div className="mission-features">
                <div className="feature-item">
                  <CheckCircle size={16} />
                  <span>Partner Empowerment</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} />
                  <span>Technology Innovation</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} />
                  <span>Global Reach</span>
                </div>
              </div>
            </div>
            <div className="vision-card">
              <div className="vision-icon">
                <Globe size={48} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To become the world's leading B2B smart home platform, connecting businesses 
                with innovative IoT solutions that transform how people live, work, and interact 
                with their environments.
              </p>
              <div className="vision-features">
                <div className="feature-item">
                  <CheckCircle size={16} />
                  <span>Global Leadership</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} />
                  <span>Innovation Hub</span>
                </div>
                <div className="feature-item">
                  <CheckCircle size={16} />
                  <span>Partner Success</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="our-values">
        <div className="container">
          <div className="section-header">
            <h2>Our Core Values</h2>
            <p>The principles that guide everything we do</p>
          </div>
          <div className="values-grid">
            <div className="value-card">
              <div className="value-icon">
                <Lightbulb size={40} />
              </div>
              <h3>Innovation</h3>
              <p>We continuously push the boundaries of smart home technology, developing cutting-edge solutions that anticipate market needs.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <Shield size={40} />
              </div>
              <h3>Trust & Security</h3>
              <p>We prioritize the security and privacy of our partners and end-users, implementing enterprise-grade security measures.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <Users size={40} />
              </div>
              <h3>Partnership</h3>
              <p>We believe in the power of collaboration, building strong relationships with our partners to achieve mutual success.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <Zap size={40} />
              </div>
              <h3>Excellence</h3>
              <p>We strive for excellence in everything we do, from product development to customer support and partner success.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <Heart size={40} />
              </div>
              <h3>Customer Focus</h3>
              <p>Our partners and their clients are at the center of everything we do, driving our decisions and innovations.</p>
            </div>
            <div className="value-card">
              <div className="value-icon">
                <TrendingUp size={40} />
              </div>
              <h3>Growth</h3>
              <p>We are committed to the growth and success of our partners, providing the tools and support they need to thrive.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="our-team">
        <div className="container">
          <div className="section-header">
            <h2>Meet Our Team</h2>
            <p>The passionate individuals behind Aeviot&apos;s success</p>
          </div>
          <div className="team-content">
            <div className="team-stats">
              <div className="team-stat">
                <div className="stat-number">50+</div>
                <div className="stat-label">Team Members</div>
              </div>
              <div className="team-stat">
                <div className="stat-number">15+</div>
                <div className="stat-label">Countries</div>
              </div>
              <div className="team-stat">
                <div className="stat-number">10+</div>
                <div className="stat-label">Languages</div>
              </div>
              <div className="team-stat">
                <div className="stat-number">5+</div>
                <div className="stat-label">Years Average Experience</div>
              </div>
            </div>
            <div className="team-description">
              <h3>Diverse Expertise, Unified Vision</h3>
              <p>
                Our team consists of world-class engineers, business strategists, and industry 
                experts who share a common passion for smart home technology. We come from diverse 
                backgrounds and bring unique perspectives to solve complex challenges in the IoT space.
              </p>
              <div className="team-highlights">
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>IoT & Hardware Engineers</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>Software Development Experts</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>Business Development Specialists</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>Customer Success Managers</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>Marketing & Sales Professionals</span>
                </div>
                <div className="highlight-item">
                  <CheckCircle size={20} />
                  <span>Quality Assurance Engineers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="awards-recognition">
        <div className="container">
          <div className="section-header">
            <h2>Awards & Recognition</h2>
            <p>Industry recognition for our innovation and excellence</p>
          </div>
          <div className="awards-grid">
            <div className="award-item">
              <div className="award-icon">
                <AwardIcon size={48} />
              </div>
              <h3>Best B2B IoT Platform 2024</h3>
              <p>Smart Home Technology Awards</p>
              <div className="award-year">2024</div>
            </div>
            <div className="award-item">
              <div className="award-icon">
                <Star size={48} />
              </div>
              <h3>Innovation Excellence Award</h3>
              <p>IoT World Congress</p>
              <div className="award-year">2023</div>
            </div>
            <div className="award-item">
              <div className="award-icon">
                <TrendingUp size={48} />
              </div>
              <h3>Fastest Growing Platform</h3>
              <p>TechCrunch Startup Awards</p>
              <div className="award-year">2023</div>
            </div>
            <div className="award-item">
              <div className="award-icon">
                <Users2 size={48} />
              </div>
              <h3>Partner Choice Award</h3>
              <p>Channel Partner Conference</p>
              <div className="award-year">2022</div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner CTA */}
      <section className="about-partner-cta" aria-labelledby="about-partner-cta-title">
        <div className="about-partner-cta__glow" aria-hidden />
        <div className="container">
          <div className="about-partner-cta__card">
            <p className="about-partner-cta__eyebrow">PARTNERSHIPS</p>
            <h2 id="about-partner-cta-title" className="about-partner-cta__title">
              Ready to Partner With Us?
            </h2>
            <p className="about-partner-cta__subtitle">
              Join our network of successful partners and start offering smart home solutions
              to your clients with our white-label platform.
            </p>
            <div className="about-partner-cta__actions">
              <PartnerPanelLink to={partnerSignupUrl()} className="btn btn-primary btn-large">
                <span>Become a Partner</span>
                <ArrowRight size={20} />
              </PartnerPanelLink>
              <Link to="/contact-us" className="btn btn-outline btn-large">
                <span>Contact Us</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      <SiteClosingCta
        titleLines={['Ready to partner', 'with Aeviot?']}
        subtitle="Join hundreds of partners building smart solutions on our platform"
        cta={{ label: 'Book Demo', to: '/contact-us' }}
      />
    </div>
  );
};
